import { PersonnelAccessModule } from "../features/personnel-access/PersonnelAccessModule";
import { WorkOrderModule } from "../features/production-work-orders/WorkOrderModule";
import {
  AdminConsole,
} from "../features/admin/AdminConsole";
import { AdminFormWorkspace } from "../features/shift-production-form/AdminFormWorkspace";
import {
  ContractsModule,
} from "../features/contracts/ContractsModule";
import {
  type ProcurementWorkspaceTarget,
} from "../features/procurement/domain/selectors";
import {
  ApprovalsModule,
} from "../features/procurement/presentation/approvals/ApprovalsModule";
import {
  OffersModule,
} from "../features/procurement/presentation/offers/OffersModule";
import {
  RequestsModule,
} from "../features/procurement/presentation/requests/RequestsModule";
import {
  ProductionExecutionModule,
} from "../features/production-execution/ProductionExecutionModule";
import {
  ProductionPerformanceReportModule,
} from "../features/production-reports/ProductionPerformanceReportModule";
import {
  ProductionRequirementsModule,
} from "../features/production-requirements/ProductionRequirementsModule";
import {
  WarehousePreparationModule,
} from "../features/production-requirements/WarehousePreparationModule";
import {
  ProfileModule,
} from "../features/profile/ProfileModule";
import {
  PurchaseOrdersModule,
} from "../features/purchase-orders/presentation/PurchaseOrdersModule";
import {
  QualityCapaModule,
} from "../features/quality-capa/QualityCapaModule";
import {
  LabelsModule,
} from "../features/labels/LabelsModule";
import {
  ReportsModule,
} from "../features/reports/ReportsModule";
import {
  SettingsModule,
} from "../features/settings/SettingsModule";
import {
  SuppliersModule,
} from "../features/suppliers/SuppliersModule";
import {
  TeamModule,
} from "../features/team/TeamModule";
import {
  can,
  navPermission,
  roleCanNavigate,
  type SessionUser,
} from "../shared/auth/access";
import {
  SystemState,
} from "../shared/components/SystemState";

type ModuleViewProps = {
  activeNav: string;
  notify: (message: string, tone?: "success" | "error") => void;
  requestComposerOpen: boolean;
  setRequestComposerOpen: (open: boolean) => void;
  user: SessionUser;
  offerFocusRequestNo: string;
  purchaseOrderFocusRequestNo: string;
  approvalFocusRequestNo: string;
  requestFocusRequestNo: string;
  supplierFocusQuery: string;
  globalNavigationVersion: number;
  dataRefreshVersion: number;
  onNavigate: (view: string) => void;
  onNestedBackChange: (handler: (() => void) | null) => void;
  onOpenRequestWorkspace: (
    requestNo: string,
    target: ProcurementWorkspaceTarget,
  ) => void;
  onOpenPurchaseOrder: (requestNo: string) => void;
  onReturnToOffers: (requestNo: string) => void;
  onSpecialistDecisionSubmitted: () => void;
  onLogout: () => Promise<void>;
  onProductionBack?: () => void;
};

function AccessDenied() {
  return (
    <SystemState
      title="Bu çalışma alanına erişiminiz yok"
      description="Oturumunuz açık ancak seçilen bölüm mevcut rolünüzün yetki kapsamında değil. Yetki değişikliği gerekiyorsa sistem yöneticinizle iletişime geçin."
      assistance="Sol menüden erişiminize açık başka bir çalışma alanı seçebilirsiniz."
    />
  );
}

export function ModuleView({
  activeNav,
  notify,
  requestComposerOpen,
  setRequestComposerOpen,
  user,
  offerFocusRequestNo,
  purchaseOrderFocusRequestNo,
  approvalFocusRequestNo,
  requestFocusRequestNo,
  supplierFocusQuery,
  globalNavigationVersion,
  dataRefreshVersion,
  onNavigate,
  onNestedBackChange,
  onOpenRequestWorkspace,
  onOpenPurchaseOrder,
  onReturnToOffers,
  onSpecialistDecisionSubmitted,
  onLogout,
  onProductionBack,
}: ModuleViewProps) {
  switch (activeNav) {
    case "Personel ve Erişim Merkezi":
      return user.role === "SISTEM_YONETICISI" && can(user, "admin.users.manage")
        ? <PersonnelAccessModule />
        : <AccessDenied />;
    case "Vardiya Formu Yönetimi":
      return user.role === "SISTEM_YONETICISI" && can(user, "admin.users.manage")
        ? <AdminFormWorkspace />
        : <AccessDenied />;
    case "Üretim":
      return (
        <ProductionExecutionModule
          user={user}
          notify={notify}
          dataRefreshVersion={dataRefreshVersion}
          onLogout={onLogout}
          onBack={onProductionBack}
        />
      );
    case "İş Emri Takibi":
      return <WorkOrderModule />;
    case "Üretim Raporları":
      return (
        <ProductionPerformanceReportModule
          notify={notify}
          dataRefreshVersion={dataRefreshVersion}
          onOpenPlanValidation={
            roleCanNavigate(user, "Malzeme Planlama") &&
              can(user, navPermission["Malzeme Planlama"])
              ? () => onNavigate("Malzeme Planlama")
              : undefined
          }
        />
      );
    case "Depo Hazırlık":
      return <WarehousePreparationModule notify={notify} dataRefreshVersion={dataRefreshVersion} user={user} />;
    case "Etiket Talepleri":
      return can(user, "label.read") ? <LabelsModule /> : <AccessDenied />;
    case "Malzeme Planlama":
      return (
        <ProductionRequirementsModule
          notify={notify}
          dataRefreshVersion={dataRefreshVersion}
          user={user}
        />
      );
    case "Talepler":
      if (user.role === "SATIN_ALMA_MUDURU" || user.role === "GENEL_MUDUR") {
        return (
          <ApprovalsModule
            notify={notify}
            user={user}
            dataRefreshVersion={dataRefreshVersion}
            initialRequestNo={approvalFocusRequestNo ?? requestFocusRequestNo}
          />
        );
      }
      return (
        <RequestsModule
          notify={notify}
          composerOpen={requestComposerOpen}
          setComposerOpen={setRequestComposerOpen}
          user={user}
          initialRequestNo={requestFocusRequestNo}
          navigationVersion={globalNavigationVersion}
          dataRefreshVersion={dataRefreshVersion}
          onNestedBackChange={onNestedBackChange}
          onOpenRequestWorkspace={onOpenRequestWorkspace}
        />
      );
    case "Teklifler":
      return (
        <OffersModule
          notify={notify}
          user={user}
          initialRequestNo={offerFocusRequestNo}
          dataRefreshVersion={dataRefreshVersion}
          onOpenPurchaseOrder={onOpenPurchaseOrder}
          onSpecialistDecisionSubmitted={onSpecialistDecisionSubmitted}
        />
      );
    case "Siparişler":
      return (
        <PurchaseOrdersModule
          notify={notify}
          user={user}
          dataRefreshVersion={dataRefreshVersion}
          initialRequestNo={purchaseOrderFocusRequestNo}
          onReturnToOffers={onReturnToOffers}
        />
      );
    case "Onay Merkezi":
      return (
        <ApprovalsModule
          notify={notify}
          user={user}
          dataRefreshVersion={dataRefreshVersion}
          initialRequestNo={approvalFocusRequestNo}
        />
      );
    case "Tedarikçiler":
      return (
        <SuppliersModule
          notify={notify}
          user={user}
          initialQuery={supplierFocusQuery}
          navigationVersion={globalNavigationVersion}
          dataRefreshVersion={dataRefreshVersion}
        />
      );
    case "Sözleşmeler":
      return <ContractsModule user={user} notify={notify} />;
    case "Raporlar":
      return (
        <ReportsModule
          notify={notify}
          dataRefreshVersion={dataRefreshVersion}
        />
      );
    case "DÖF Yönetimi":
      return (
        <QualityCapaModule
          user={user}
          notify={notify}
          dataRefreshVersion={dataRefreshVersion}
        />
      );
    case "Kullanıcılar":
      return (
        <TeamModule notify={notify} dataRefreshVersion={dataRefreshVersion} />
      );
    case "Sistem Ayarları":
      return (
        <SettingsModule
          notify={notify}
          dataRefreshVersion={dataRefreshVersion}
        />
      );
    case "Profilim":
      return <ProfileModule user={user} notify={notify} />;
    case "Sistem Genel Bakış":
    case "Roller ve Yetkiler":
    case "Departmanlar":
    case "Onay Akışları":
    case "Microsoft 365":
    case "E-posta Bildirimleri":
    case "Aktif Oturumlar":
    case "Güvenlik Kayıtları":
      return <AdminConsole page={activeNav} notify={notify} />;
    default:
      return <AccessDenied />;
  }
}
